// ============================================================
// MoveList — Plain-language move list for free explore mode
// No chess notation — descriptive text only
// ============================================================

import { useRef, useEffect } from "react"
import type { MoveData, PieceName } from "@shared/types"
import styles from "./MoveList.module.css"

interface MoveListProps {
  moves: MoveData[]
  currentMoveIndex: number
  onGoToMove: (index: number) => void
}

/** Convert piece name to display-friendly format */
function pieceName(piece: PieceName): string {
  return piece.charAt(0).toUpperCase() + piece.slice(1)
}

/** Convert a move to plain language description */
function describeMove(move: MoveData): string {
  const piece = pieceName(move.piece)

  if (move.san === "O-O") return "King castles short"
  if (move.san === "O-O-O") return "King castles long"

  if (move.promotion) {
    const promo = pieceName(move.promotion)
    if (move.captured) {
      return `${piece} captures on ${move.to}, promotes to ${promo}`
    }
    return `${piece} to ${move.to}, promotes to ${promo}`
  }

  if (move.captured) {
    const captured = pieceName(move.captured)
    return `${piece} captures ${captured.toLowerCase()} on ${move.to}`
  }

  return `${piece} to ${move.to}`
}

export const MoveList = ({
  moves,
  currentMoveIndex,
  onGoToMove,
}: MoveListProps) => {
  const activeRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll active move into view
  useEffect(() => {
    if (activeRef.current && listRef.current) {
      activeRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      })
    }
  }, [currentMoveIndex])

  // Group moves into pairs (white + black)
  const movePairs: { number: number; white: MoveData | null; black: MoveData | null }[] = []
  for (let i = 0; i < moves.length; i++) {
    const move = moves[i]
    if (move.color === "white") {
      movePairs.push({
        number: move.moveNumber,
        white: move,
        black: moves[i + 1]?.color === "black" ? moves[i + 1] : null,
      })
    }
  }

  return (
    <div className={styles.container} ref={listRef} role="list" aria-label="Move list">
      {movePairs.map((pair) => {
        const whiteIndex = moves.findIndex(
          (m) => m.moveNumber === pair.number && m.color === "white"
        )
        const blackIndex = pair.black
          ? moves.findIndex(
              (m) => m.moveNumber === pair.number && m.color === "black"
            )
          : -1

        return (
          <div key={pair.number} className={styles.movePair} role="listitem">
            <span className={styles.moveNumber}>{pair.number}.</span>
            {pair.white && (
              <button
                ref={currentMoveIndex === whiteIndex ? activeRef : null}
                className={`${styles.moveButton} ${
                  currentMoveIndex === whiteIndex ? styles.activeMove : ""
                }`}
                onClick={() => onGoToMove(whiteIndex)}
              >
                {describeMove(pair.white)}
              </button>
            )}
            {pair.black && (
              <button
                ref={currentMoveIndex === blackIndex ? activeRef : null}
                className={`${styles.moveButton} ${
                  currentMoveIndex === blackIndex ? styles.activeMove : ""
                }`}
                onClick={() => onGoToMove(blackIndex)}
              >
                {describeMove(pair.black)}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
